import { ReactElement, useMemo } from 'react';

import { PatientGroup } from '@dreryk/egabinet-patient-frontend-api';

import { BindingCreator, useTranslate } from '@dreryk/react-common';
import { FilterPanel, HexColorDotSize, Multiselect, StyledFlex, Text } from '@dreryk/react-components';

import {
   PatientFilterPanelFormBean,
   PatientFilterPanelSearchParam,
   PatientFilterPanelStorageKey,
} from '../../PatientFilterPanel.types';
import { filtersPanelTranslationKeys } from '../../../../translations';

import {
   createPatientGroupsCodec,
   createPatientGroupsTriggerLabelExtractor,
   patientGroupsDeselectorsPropsMapper,
} from './PatientGroupsController.utils';
import { StyledColorDot, StyledPatientGroupsMultiselect } from './PatientGroupsController.styled';

type PractitionersControllerProps = {
   registerController: BindingCreator<PatientFilterPanelFormBean, 'patientGroups'>;
   patientGroups: PatientGroup[];
};

export const PatientGroupsController = (props: PractitionersControllerProps): ReactElement => {
   const translate = useTranslate();
   const codec = useMemo(() => createPatientGroupsCodec(props.patientGroups), [props.patientGroups]);

   return (
      <FilterPanel.Controller<PatientFilterPanelFormBean, 'patientGroups'>
         name={'patientGroups'}
         searchParam={PatientFilterPanelSearchParam.PATIENT_GROUPS}
         storageKey={PatientFilterPanelStorageKey.PATIENT_GROUPS}
         codec={codec}
         chipsLabel={filtersPanelTranslationKeys.patientGroups.patientGroup}
         getDeselectorsProps={patientGroupsDeselectorsPropsMapper}
      >
         {({ applyFilter }) => (
            <StyledPatientGroupsMultiselect<PatientGroup>
               testId={'patientGroups'}
               triggerLabelExtractor={createPatientGroupsTriggerLabelExtractor(props.patientGroups.length, translate)}
               controller={props.registerController('patientGroups')}
               onHide={applyFilter}
               label={filtersPanelTranslationKeys.patientGroups.patientGroup}
               placeholder={filtersPanelTranslationKeys.patientGroups.choosePatientGroups}
               itemKeyGenerator={(patientGroup) => patientGroup.id}
               compact
            >
               <>
                  <Multiselect.SelectAll
                     testId={'selectAll'}
                     items={props.patientGroups}
                     selectAllLabel={filtersPanelTranslationKeys.patientGroups.allOptions}
                  />
                  <Multiselect.List<PatientGroup> items={props.patientGroups} keyExtractor={'id'}>
                     {(item) => (
                        <Multiselect.Item
                           item={item}
                           label={
                              <StyledFlex gap={4}>
                                 <StyledColorDot
                                    key={item.id}
                                    testId={item.id}
                                    color={item.primaryColor.asHexString()}
                                    darkModeColor={item.darkModeColor?.asHexString()}
                                    size={HexColorDotSize.SMALL}
                                 />

                                 <Text testId={'name'}>{item.name}</Text>
                              </StyledFlex>
                           }
                           testId={item.id}
                        />
                     )}
                  </Multiselect.List>
               </>
            </StyledPatientGroupsMultiselect>
         )}
      </FilterPanel.Controller>
   );
};
